import React, { useState, useMemo } from "react";
import "./styles/TrendChart.css";

const VIEW_WIDTH = 720;
const VIEW_HEIGHT = 220;
const PADDING = { top: 12, right: 8, bottom: 22, left: 44 };

const PLOT_WIDTH = VIEW_WIDTH - PADDING.left - PADDING.right;
const PLOT_HEIGHT = VIEW_HEIGHT - PADDING.top - PADDING.bottom;

/**
 * Pick a rounded axis maximum so the gridline labels are readable numbers
 * (0 / 50 / 100) instead of whatever the tallest bar happens to be.
 */
const niceCeiling = (value) => {
  if (value <= 0) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const normalised = value / magnitude;
  let step;
  if (normalised <= 1) step = 1;
  else if (normalised <= 2) step = 2;
  else if (normalised <= 5) step = 5;
  else step = 10;
  return step * magnitude;
};

/**
 * TrendChart - Area chart for a single series over time.
 *
 * @param {Object} props
 * @param {Array} props.data - Points of { date, value, meta }
 * @param {Function} props.formatValue - Formats the y value for axis + tooltip
 * @param {Function} props.formatDate - Formats the x value for the tooltip
 * @param {Function} props.formatAxisDate - Formats the x axis ticks
 * @param {string} props.metaLabel - Suffix for the point's secondary figure
 * @param {string} props.emptyMessage - Shown when every point is zero
 */
const TrendChart = ({
  data = [],
  formatValue = (v) => String(v),
  formatDate = (d) => String(d),
  formatAxisDate = (d) => String(d),
  metaLabel,
  emptyMessage = "No activity in this period",
}) => {
  const [activeIndex, setActiveIndex] = useState(null);

  const chart = useMemo(() => {
    if (!data.length) return null;

    const maxValue = Math.max(...data.map((point) => point.value || 0));
    const ceiling = niceCeiling(maxValue);

    // A single point has no width to span, so it is pinned to the centre
    // rather than dividing by zero.
    const xFor = (index) =>
      data.length === 1
        ? PADDING.left + PLOT_WIDTH / 2
        : PADDING.left + (index / (data.length - 1)) * PLOT_WIDTH;
    const yFor = (value) =>
      PADDING.top + PLOT_HEIGHT - ((value || 0) / ceiling) * PLOT_HEIGHT;

    const points = data.map((point, index) => ({
      ...point,
      x: xFor(index),
      y: yFor(point.value),
    }));

    const line = points
      .map((point, index) => `${index === 0 ? "M" : "L"}${point.x} ${point.y}`)
      .join(" ");

    const baseline = PADDING.top + PLOT_HEIGHT;
    const area = `${line} L${points[points.length - 1].x} ${baseline} L${points[0].x} ${baseline} Z`;

    const gridValues = [0, 0.5, 1].map((fraction) => ceiling * fraction);

    // Roughly six ticks, whatever the range length, always including the
    // last day so the axis ends where the data ends.
    const tickStep = Math.max(1, Math.ceil(data.length / 6));
    const tickIndexes = [];
    for (let i = 0; i < data.length; i += tickStep) tickIndexes.push(i);
    if (tickIndexes[tickIndexes.length - 1] !== data.length - 1) {
      tickIndexes.push(data.length - 1);
    }

    return { points, line, area, gridValues, tickIndexes, ceiling, baseline };
  }, [data]);

  if (!chart) {
    return <div className="dsTrendEmpty">{emptyMessage}</div>;
  }

  const allZero = data.every((point) => !point.value);
  const active = activeIndex === null ? null : chart.points[activeIndex];
  const bandWidth = PLOT_WIDTH / Math.max(1, data.length);

  return (
    <div className="dsTrendChart">
      <svg
        className="dsTrendChartSvg"
        viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Trend over ${data.length} days`}
        onMouseLeave={() => setActiveIndex(null)}
      >
        <defs>
          <linearGradient id="dsTrendGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" className="dsTrendGradientTop" />
            <stop offset="100%" className="dsTrendGradientBottom" />
          </linearGradient>
        </defs>

        {chart.gridValues.map((value) => {
          const y =
            PADDING.top + PLOT_HEIGHT - (value / chart.ceiling) * PLOT_HEIGHT;
          return (
            <g key={value}>
              <line
                className="dsTrendGrid"
                x1={PADDING.left}
                y1={y}
                x2={VIEW_WIDTH - PADDING.right}
                y2={y}
              />
              <text
                className="dsTrendAxisLabel"
                x={PADDING.left - 8}
                y={y + 3}
                textAnchor="end"
              >
                {formatValue(value, true)}
              </text>
            </g>
          );
        })}

        {!allZero && (
          <React.Fragment>
            <path className="dsTrendArea" d={chart.area} />
            <path className="dsTrendLine" d={chart.line} />
          </React.Fragment>
        )}

        {chart.tickIndexes.map((index) => (
          <text
            key={index}
            className="dsTrendAxisLabel"
            x={chart.points[index].x}
            y={VIEW_HEIGHT - 6}
            textAnchor="middle"
          >
            {formatAxisDate(data[index].date)}
          </text>
        ))}

        {active && (
          <g>
            <line
              className="dsTrendMarkerLine"
              x1={active.x}
              y1={PADDING.top}
              x2={active.x}
              y2={chart.baseline}
            />
            <circle
              className="dsTrendMarkerDot"
              cx={active.x}
              cy={active.y}
              r="4"
            />
          </g>
        )}

        {chart.points.map((point, index) => (
          <rect
            key={index}
            className="dsTrendHitArea"
            x={point.x - bandWidth / 2}
            y={PADDING.top}
            width={bandWidth}
            height={PLOT_HEIGHT}
            onMouseEnter={() => setActiveIndex(index)}
          />
        ))}
      </svg>

      {active && (
        <div
          className="dsTrendTooltip"
          style={{
            left: `${(active.x / VIEW_WIDTH) * 100}%`,
            top: `${(active.y / VIEW_HEIGHT) * 100}%`,
          }}
        >
          <span className="dsTrendTooltipDate">{formatDate(active.date)}</span>
          <span className="dsTrendTooltipValue">
            {formatValue(active.value)}
          </span>
          {active.meta !== undefined && (
            <span className="dsTrendTooltipMeta">
              {active.meta} {metaLabel}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default TrendChart;
